import {
  ExpressionSpecification,
  FilterSpecification,
  LayerProps,
} from 'shared/map-exports';
import {
  FloodWay100PatternIcon,
  FloodWay500PatternIcon,
  FloodWayLeveePatternIcon,
  FloodWayNoCoveragePatternIcon,
  FloodWayNoCoverageSelectedPatternIcon,
  FloodWayPatternIcon,
  FloodWaySelectedPatternIcon,
  SelectedPolygonPattern,
} from 'features/mapping/icons';
import {
  FEMA_FLOOD_HAZARD_FILL_LAYER_ID,
  FEMA_FLOOD_HAZARD_PATTERN_LAYER_ID,
  FEMA_FLOOD_HAZARD_STROKE_LAYER_ID,
  FLOOD_HAZARD_COLOR,
  FLOOD_UNDETERMINED_COLOR,
  MIN_ZOOM_LEVEL,
  SOURCE_LAYER,
} from './FemaFloodHazardLayer.constants';

const allowedRiskCategories = [
  'regulatory_floodway',
  'special_floodway',
  'flood_hazard_1_pct',
  'flood_hazard_0_2_pct',
  'risk_due_to_levee',
  'undetermined',
];

const riskCategoryFilterSpecification: FilterSpecification = [
  'in',
  ['get', 'riskCategory'],
  ['literal', allowedRiskCategories],
];

const undeterminedFillOpacity = (isDarkBaseLayer: boolean): number =>
  isDarkBaseLayer ? 0.3 : 0.1;

export const getLayerFillStyle = (isDarkBaseLayer: boolean): LayerProps => ({
  id: FEMA_FLOOD_HAZARD_FILL_LAYER_ID,
  type: 'fill',
  'source-layer': SOURCE_LAYER,
  minzoom: MIN_ZOOM_LEVEL,
  paint: {
    'fill-color': [
      'match',
      ['get', 'riskCategory'],
      'undetermined',
      FLOOD_UNDETERMINED_COLOR,
      FLOOD_HAZARD_COLOR,
    ],
    'fill-opacity': [
      'match',
      ['get', 'riskCategory'],
      'flood_hazard_1_pct',
      0.6,
      'flood_hazard_0_2_pct',
      0.4,
      'undetermined',
      undeterminedFillOpacity(isDarkBaseLayer),
      0,
    ],
  },
  filter: riskCategoryFilterSpecification,
});

const undeterminedStrokeOpacity = (isDarkBaseLayer: boolean): number =>
  isDarkBaseLayer ? 0.3 : 0.2;

export const getStrokeStyle = (isDarkBaseLayer: boolean): LayerProps => ({
  id: FEMA_FLOOD_HAZARD_STROKE_LAYER_ID,
  type: 'line',
  'source-layer': SOURCE_LAYER,
  minzoom: MIN_ZOOM_LEVEL,
  paint: {
    'line-color': [
      'match',
      ['get', 'riskCategory'],
      'flood_hazard_1_pct',
      FLOOD_HAZARD_COLOR,
      'undetermined',
      FLOOD_UNDETERMINED_COLOR,
      FLOOD_HAZARD_COLOR,
    ],
    'line-opacity': [
      'match',
      ['get', 'riskCategory'],
      'flood_hazard_1_pct',
      0.75,
      'undetermined',
      undeterminedStrokeOpacity(isDarkBaseLayer),
      0,
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.25, 18, 3.25],
  },
  filter: riskCategoryFilterSpecification,
});

const undeterminedPatternOpacity = (isDarkBaseLayer: boolean): number =>
  isDarkBaseLayer ? 0.2 : 0.05;

export const getPatternFillStyle = (isDarkBaseLayer: boolean): LayerProps => ({
  id: FEMA_FLOOD_HAZARD_PATTERN_LAYER_ID,
  type: 'fill',
  'source-layer': SOURCE_LAYER,
  minzoom: MIN_ZOOM_LEVEL,
  paint: {
    'fill-pattern': [
      'match',
      ['get', 'riskCategory'],
      'flood_hazard_1_pct',
      FloodWay100PatternIcon.name,
      'flood_hazard_0_2_pct',
      FloodWay500PatternIcon.name,
      'risk_due_to_levee',
      FloodWayLeveePatternIcon.name,
      'undetermined',
      FloodWayNoCoveragePatternIcon.name,
      // regulatory / special floodway as default
      FloodWayPatternIcon.name,
    ],
    'fill-opacity': [
      'match',
      ['get', 'riskCategory'],
      ['flood_hazard_1_pct', 'flood_hazard_0_2_pct', 'risk_due_to_levee'],
      1,
      ['regulatory_floodway', 'special_floodway'],
      0.9,
      'undetermined',
      undeterminedPatternOpacity(isDarkBaseLayer),
      // default for any other category
      0.05,
    ],
  },
  filter: riskCategoryFilterSpecification,
});

export const selectedPatternExpression: ExpressionSpecification = [
  'match',
  ['get', 'riskCategory'],
  ['flood_hazard_1_pct', 'flood_hazard_0_2_pct'],
  SelectedPolygonPattern.name,
  'undetermined',
  FloodWayNoCoverageSelectedPatternIcon.name,
  // regulatory / special, risk_due_to_levee as default
  FloodWaySelectedPatternIcon.name,
];
